import React, { useRef } from "react";
import { ALL_FILE_TYPE } from "src/constants/validation";
import { colors } from "src/styles";
import { MaximoDocument } from "src/types";
import styled from "styled-components";

interface AttachmentPickerModalProps {
  isOpen: boolean;
  files: MaximoDocument[];
  subtitle: string;
  confirmText?: string;
  onClose: () => void;
  onConfirm: () => void;
  onRemove: (index: number) => void;
  onFilesSelected: (files: FileList | null) => void | Promise<void>;
  error?: string;
}

export const AttachmentPickerModal = ({
  isOpen,
  files,
  subtitle,
  confirmText = "Done",
  onClose,
  onConfirm,
  onRemove,
  onFilesSelected,
  error,
}: AttachmentPickerModalProps) => {
  const hiddenFileInput = useRef<HTMLInputElement>(null);

  if (!isOpen) {
    return null;
  }

  return (
    <AttachmentModalOverlay>
      <AttachmentModalCard>
        <AttachmentModalHeader>
          <div>
            <h3>Attach Files</h3>
            <AttachmentModalSubTitle>{subtitle}</AttachmentModalSubTitle>
          </div>
          <AttachmentModalCloseButton type="button" onClick={onClose}>
            Close
          </AttachmentModalCloseButton>
        </AttachmentModalHeader>

        <AttachmentModalBody>
          <AttachmentDropZone>
            <AttachmentDropZoneTitle>Choose one or more files</AttachmentDropZoneTitle>
            <AttachmentDropZoneText>
              Images, PDFs, Word files, spreadsheets, and other work order attachments.
            </AttachmentDropZoneText>
            <AttachmentBrowseButton type="button" onClick={() => hiddenFileInput.current?.click()}>
              Browse Files
            </AttachmentBrowseButton>
            <input
              ref={hiddenFileInput}
              type="file"
              multiple
              accept={ALL_FILE_TYPE}
              style={{ display: "none" }}
              onChange={(e) => {
                void onFilesSelected(e.target.files);
                e.target.value = "";
              }}
            />
          </AttachmentDropZone>

          <AttachmentSelectedHeader>
            <span>Selected Files</span>
            <span>{files.length}</span>
          </AttachmentSelectedHeader>
          {files.length > 0 ? (
            <AttachmentSelectedList>
              {files.map((file, index) => (
                <AttachmentSelectedItem key={`${file.fileName}-${file.file?.size || 0}-${index}`}>
                  <div style={{ minWidth: 0 }}>
                    <AttachmentSelectedFileName>{file.fileName}</AttachmentSelectedFileName>
                    <AttachmentSelectedFileMeta>
                      {file.file ? `${Math.ceil(file.file.size / 1024)} KB` : ""}
                    </AttachmentSelectedFileMeta>
                  </div>
                  <AttachmentSelectedRemoveButton type="button" onClick={() => onRemove(index)}>
                    Remove
                  </AttachmentSelectedRemoveButton>
                </AttachmentSelectedItem>
              ))}
            </AttachmentSelectedList>
          ) : (
            <AttachmentSelectedEmpty>No files selected yet.</AttachmentSelectedEmpty>
          )}
          {error ? <AttachmentErrorMessage>{error}</AttachmentErrorMessage> : null}
        </AttachmentModalBody>

        <AttachmentModalFooter>
          <AttachmentModalCancelButton type="button" onClick={onClose}>
            Cancel
          </AttachmentModalCancelButton>
          <AttachmentModalActionButton type="button" onClick={onConfirm}>
            {confirmText}
          </AttachmentModalActionButton>
        </AttachmentModalFooter>
      </AttachmentModalCard>
    </AttachmentModalOverlay>
  );
};

const AttachmentModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 10002;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const AttachmentModalCard = styled.div`
  width: min(760px, 100%);
  max-height: 92vh;
  overflow: auto;
  background: ${colors.white.default};
  border: 1px solid ${colors.grey.light12};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
`;

const AttachmentModalHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  background: ${colors.white.default};
  border-bottom: 1px solid ${colors.grey.light12};
  padding: 14px 18px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;

  h3 {
    margin: 0;
    font-size: 18px;
    color: ${colors.black.light2};
  }
`;

const AttachmentModalSubTitle = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  color: ${colors.grey.default};
  line-height: 1.5;
`;

const AttachmentModalCloseButton = styled.button`
  border: 1px solid ${colors.grey.light12};
  background: ${colors.white.default};
  color: ${colors.black.light2};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 8px 12px;
  cursor: pointer;
`;

const AttachmentModalBody = styled.div`
  padding: 16px 18px 20px;
`;

const AttachmentDropZone = styled.div`
  border: 1px dashed ${colors.grey.light12};
  background: #fafafa;
  padding: 18px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`;

const AttachmentDropZoneTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${colors.black.light2};
`;

const AttachmentDropZoneText = styled.div`
  font-size: 12px;
  color: ${colors.grey.default};
  line-height: 1.5;
`;

const AttachmentBrowseButton = styled.button`
  border: 1px solid ${colors.black.light2};
  background: ${colors.black.light2};
  color: ${colors.white.default};
  padding: 10px 14px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  cursor: pointer;
`;

const AttachmentSelectedHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 18px 0 10px;
  font-size: 12px;
  font-weight: 700;
  color: ${colors.black.light2};
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const AttachmentSelectedList = styled.div`
  display: grid;
  gap: 8px;
`;

const AttachmentSelectedItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border: 1px solid ${colors.grey.light12};
  background: ${colors.white.default};
  padding: 12px 14px;
`;

const AttachmentSelectedFileName = styled.div`
  font-size: 13px;
  color: ${colors.black.light2};
  word-break: break-word;
`;

const AttachmentSelectedFileMeta = styled.div`
  font-size: 11px;
  color: ${colors.grey.default};
  white-space: nowrap;
`;

const AttachmentSelectedEmpty = styled.div`
  border: 1px dashed ${colors.grey.light12};
  padding: 12px 14px;
  font-size: 12px;
  color: ${colors.grey.default};
  background: #fafafa;
`;

const AttachmentSelectedRemoveButton = styled.button`
  border: 1px solid ${colors.grey.light12};
  background: ${colors.white.default};
  color: ${colors.black.light2};
  padding: 8px 12px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  cursor: pointer;
  flex-shrink: 0;
`;

const AttachmentErrorMessage = styled.div`
  margin-top: 12px;
  font-size: 12px;
  color: ${colors.red.default};
`;

const AttachmentModalFooter = styled.div`
  padding: 0 18px 18px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
`;

const AttachmentModalCancelButton = styled.button`
  border: 1px solid ${colors.grey.light12};
  background: ${colors.white.default};
  color: ${colors.black.light2};
  padding: 10px 14px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  cursor: pointer;
`;

const AttachmentModalActionButton = styled.button`
  border: 1px solid ${colors.black.light2};
  background: ${colors.black.light2};
  color: ${colors.white.default};
  padding: 10px 14px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  cursor: pointer;
`;
